import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useTranslation } from "react-i18next";
import {
  CreateOrganizationPayload,
  UpdateOrganizationPayload,
} from "../api/models/organization.models";
import {
  createOrganization,
  deleteCompanyOrganization,
  updateOrganization,
} from "../api/organization.api";

const useOrganizations = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [bearerToken, toast, companyId] = useUIStore(
    useShallow((state: UIState) => [
      state.bearerToken,
      state.toastRef,
      state.companyId,
    ])
  );

  // Create new organization
  const createNewOrganization = async (company: CreateOrganizationPayload) => {
    const result = await createNewOrganizationMutation.mutateAsync(company);
    return result;
  };

  const createNewOrganizationMutation = useMutation({
    mutationFn: (payload: CreateOrganizationPayload) =>
      createOrganization(bearerToken as string, payload),
    onError: (error) => {
      toast?.current?.show({
        severity: "error",
        detail: t("text-organization-create-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: "success",
        detail: t("text-organization-created"),
        life: 3000,
      });

      queryClient.invalidateQueries({
        queryKey: ["companies"],
      });
      queryClient.invalidateQueries({
        queryKey: ["organizations"],
      });
    },
  });

  const updateExistingOrganization = (
    company: UpdateOrganizationPayload,
    callback?: () => void
  ) => {
    updateOrganizationMutation.mutate(company, {
      onSuccess: () => {
        if (callback) {
          callback();
        }
      },
    });
  };
  const updateOrganizationMutation = useMutation({
    mutationFn: (payload: UpdateOrganizationPayload) =>
      updateOrganization(bearerToken as string, payload),
    onError: (error) => {
      toast?.current?.show({
        severity: "error",
        detail: t("text-organization-update-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: "success",
        detail: t("text-organization-updated"),
        life: 3000,
      });

      queryClient.invalidateQueries({
        queryKey: ["companies"],
      });
    },
  });

  const deleteOrganization = async (id: number) => {
    return await deleteOrganizationMutation.mutateAsync(id);
  };

  const deleteOrganizationMutation = useMutation({
    mutationFn: (id: number) =>
      deleteCompanyOrganization(bearerToken as string, companyId as number, id),
    onError: (error) => {
      toast?.current?.show({
        severity: "error",
        detail: t("text-organization-delete-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: "success",
        detail: t("text-organization-deleted"),
        life: 3000,
      });

      queryClient.invalidateQueries({
        queryKey: ["companies"],
      });
      queryClient.invalidateQueries({
        queryKey: ["organizations"],
      });
    },
  });

  const isCreatingOrganization = createNewOrganizationMutation.isPending;
  const isUpdatingOrganization = updateOrganizationMutation.isPending;
  const isDeletingOrganization = deleteOrganizationMutation.isPending;

  return {
    createNewOrganization,
    isCreatingOrganization,
    updateExistingOrganization,
    isUpdatingOrganization,
    deleteOrganization,
    isDeletingOrganization,
  };
};

export default useOrganizations;
